/**
 * @fileoverview ScrollToTop Component - Automatic scroll management on route changes
 * 
 * Provides automatic scroll management for navigation:
 * - Scroll to top on route changes
 * - Hash-based anchor navigation support
 * - Smooth scrolling with fallback
 * - Delayed execution for DOM readiness
 * - Accessibility improvements
 * - Route change detection
 * 
 * @component
 * @example
 * <ScrollToTop />
 * 
 * @requires React
 * @requires react-router-dom
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop Component
 * 
 * Automatically manages scroll position on route changes and supports anchor navigation.
 * 
 * Features:
 * - Scroll to top on route changes
 * - Hash-based anchor navigation
 * - Smooth scrolling with fallbacks
 * - Delayed execution for DOM readiness
 * - Error handling for scroll operations
 * 
 * @component
 * @returns {null} This component doesn't render anything
 */
const ScrollToTop = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        // The delayed retry below exists because a hash target may not be in the
        // DOM yet on the first pass. But pages keep loading content for a while
        // after mount, so that retry used to fire *after* the user had started
        // reading and yank them back to the top. Track real user scroll intent
        // and skip the retry once they've moved. We listen for wheel/touch/key
        // rather than 'scroll' because our own scrollTo also fires 'scroll'.
        let userHasScrolled = false;
        const markUserScroll = () => {
            userHasScrolled = true;
        };

        const scrollToElement = (isRetry = false) => {
            if (isRetry && userHasScrolled) return;

            if (hash) {
                // Remove the '#' from the hash
                const id = hash.replace('#', '');
                const element = document.getElementById(id);
                
                if (element) {
                    // Try scrolling with smooth behavior first
                    try {
                        element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    } catch (error) {
                        // Fallback to regular scroll if smooth scroll fails
                        element.scrollIntoView();
                    }
                }
            } else {
                window.scrollTo(0, 0);
            }
        };

        // Try immediately
        scrollToElement();

        window.addEventListener('wheel', markUserScroll, { passive: true });
        window.addEventListener('touchmove', markUserScroll, { passive: true });
        window.addEventListener('keydown', markUserScroll);

        // And also after a short delay to ensure DOM is ready (skipped if the
        // user has already scrolled themselves).
        const timeoutId = setTimeout(() => scrollToElement(true), 500);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('wheel', markUserScroll);
            window.removeEventListener('touchmove', markUserScroll);
            window.removeEventListener('keydown', markUserScroll);
        };
    }, [pathname, hash]);

    return null;
};

export default ScrollToTop;
